import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  customerFacingAreaLabel,
  customerFacingCameraLabel,
  customerFacingSafetyRuleLabel,
  resolveCustomerFacingPatternLabel,
} from './workspaceUtils';
import {
  ADVANCED_FILTERS_HEADING,
  ALL_AREAS_LABEL,
  ALL_CONFIGURED_POLICIES_LABEL,
  AREA_FIELD_HELPER,
  AREA_FIELD_HELPER_SCOPED,
  AREA_FIELD_LABEL,
  CAMERA_FIELD_HELPER,
  CAMERA_FIELD_HELPER_SCOPED,
  CAMERA_FIELD_LABEL,
  CHANGE_REPORT_SCOPE_BUTTON,
  CHANGE_SCOPE_INTRO,
  EVIDENCE_CASE_HEADING,
  HIDE_REPORT_SCOPE_BUTTON,
  REPORTING_ON_HEADING,
  REPORT_SCOPE_INTRO_COLLAPSED,
  REPORT_SCOPE_INTRO_EXPANDED,
  REPORT_SCOPE_PANEL_TITLE,
  SCOPE_FROM_REVIEW_NOTE,
  SCOPE_HIERARCHY_NOTE,
  SCOPE_HIERARCHY_NOTE_HAZARD_CLASS,
  REPRESENTATIVE_CASE_HAZARD_CLASS_NOTE,
  SAFETY_POLICY_FIELD_HELPER,
  SAFETY_POLICY_FIELD_HELPER_SCOPED,
  SAFETY_POLICY_FIELD_LABEL,
  reportScopeLinesAreComplete,
  type ReportScopeDisplayLine,
  type ReportScopeHierarchySummary,
} from './safetyReportFilterCopy';
import {
  decorateRepresentativeCaseCandidates,
  formatRepresentativeSeenLine,
  limitRepresentativeCaseCandidates,
  buildRepresentativeCaseListHint,
  REPRESENTATIVE_CASE_AUTO_HELPER,
  REPRESENTATIVE_CASE_FILTER_HELPER,
  REPRESENTATIVE_CASE_FILTER_LABEL,
  REPRESENTATIVE_CASE_FILTER_PLACEHOLDER,
  REPRESENTATIVE_CASE_LIST_LABEL,
  REPRESENTATIVE_CASE_LIST_PLACEHOLDER,
  REPRESENTATIVE_CASE_PICKER_INTRO,
  REPRESENTATIVE_SAFETY_CASE_LABEL,
  type RepresentativeSafetyCaseOption,
} from './representativeSafetyCaseSelection';
import SafetyReportTypeControls from './SafetyReportTypeControls';
import type { HazardClassKey, SafetyReportTypeMode } from './hazardClassReportOptions';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';

export type { RepresentativeSafetyCaseOption as ReviewIncidentOption };

type SelectOption = {
  value: string;
  label: string;
};

type Props = {
  reviewRecordPolicyFilter: string;
  onReviewRecordPolicyFilterChange: (value: string) => void;
  reviewRecordPolicyOptions: SelectOption[];
  reviewRecordCameraFilter: string;
  onReviewRecordCameraFilterChange: (value: string) => void;
  reviewRecordCameraOptions: SelectOption[];
  filteredSelectableReviewIncidents: RepresentativeSafetyCaseOption[];
  activeRepresentativeCase: RepresentativeSafetyCaseOption | null;
  selectedReviewIncidentIds: string[];
  onSelectedReviewIncidentIdsChange: (ids: string[]) => void;
  reviewScopeIncidentsLoading: boolean;
  selectedIncidentScopeIsValid: boolean;
  selectedIncidentScopeInvalidReason: string;
  proofScopePolicyTypeDraft: string;
  proofScopePolicySelectValue: string;
  onProofScopePolicyTypeDraftChange: (value: string) => void;
  policyTypeOptions: SelectOption[];
  reportFiltersScopedToCamera: boolean;
  proofScopeZoneDraft: string;
  onProofScopeZoneDraftChange: (value: string) => void;
  zoneOptions: SelectOption[];
  proofScopeFromDraft: string;
  onProofScopeFromDraftChange: (value: string) => void;
  proofScopeToDraft: string;
  onProofScopeToDraftChange: (value: string) => void;
  currentSelectionSummary: string;
  reportScopeHierarchy: ReportScopeHierarchySummary;
  scopeCarriedFromReview: boolean;
  reportScopeDisplayLines: ReportScopeDisplayLine[];
  reportScopeResolutionHelper?: string | null;
  reviewRecordCameraSelectValue: string;
  reviewRecordCameraOptionsResolved: SelectOption[];
  filtersExpanded: boolean;
  onToggleFiltersExpanded: () => void;
  onApplyProofScopeSelection: () => void;
  proofScopeDirty: boolean;
  disableUpdateReport: boolean;
  onResetFilters: () => void;
  onOpenSelectedIncident: () => void;
  selectedIncidentReviewHref: string;
  onBackToReview: () => void;
  safetyReportTypeMode: SafetyReportTypeMode;
  onSafetyReportTypeModeChange: (mode: SafetyReportTypeMode) => void;
  hazardClassKey: HazardClassKey;
  onHazardClassKeyChange: (key: HazardClassKey) => void;
};

function CompactReportScopeSummary({
  lines,
  scopeCarriedFromReview,
}: {
  lines: ReportScopeDisplayLine[];
  scopeCarriedFromReview: boolean;
}) {
  const scopeComplete = reportScopeLinesAreComplete(lines);
  return (
    <Box data-testid="compact-report-scope-summary">
      {scopeCarriedFromReview ? (
        <Alert severity="info" sx={{ mb: 1, py: 0.35 }}>
          {SCOPE_FROM_REVIEW_NOTE}
        </Alert>
      ) : null}
      {!scopeComplete ? (
        <Alert severity="warning" sx={{ mb: 1, py: 0.35 }}>
          Scope incomplete. Select a risk pattern, area, camera group, time window, and representative evidence before signing this Draft Safety Scan.
        </Alert>
      ) : null}
      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
        {lines.map((line) => (
          <Typography key={line.label} component="li" variant="body2" sx={{ lineHeight: 1.5, mb: 0.35 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{line.label}: </Box>
            {line.value}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function ReportScopeHierarchyPanel({
  hierarchy,
  scopeCarriedFromReview,
  hazardClassMode = false,
}: {
  hierarchy: ReportScopeHierarchySummary;
  scopeCarriedFromReview: boolean;
  hazardClassMode?: boolean;
}) {
  const hierarchyRows = [
    { label: displaySafetyManagerLabel('report_mode_pattern'), value: hierarchy.patternLabel },
    { label: 'Camera group', value: hierarchy.cameraLabel },
    { label: 'Area', value: hierarchy.areaLabel },
    { label: 'Time window', value: hierarchy.timeWindowLabel },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 1.15, borderRadius: 2, bgcolor: 'action.hover' }}>
      {scopeCarriedFromReview ? (
        <Alert severity="info" sx={{ mb: 1, py: 0.35 }}>
          {SCOPE_FROM_REVIEW_NOTE}
        </Alert>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.04, textTransform: 'uppercase' }}>
        {REPORTING_ON_HEADING}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 700, lineHeight: 1.45 }}>
        {hierarchy.reportingOnLine}
      </Typography>
      <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.25 }}>
        {hierarchyRows.map((row) => (
          <Typography key={row.label} component="li" variant="body2" sx={{ lineHeight: 1.5, mb: 0.3 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{row.label}: </Box>
            {row.value}
          </Typography>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.04, textTransform: 'uppercase' }}>
        {EVIDENCE_CASE_HEADING}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.35, lineHeight: 1.45 }}>
        {hierarchy.evidenceCaseLine}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.85, lineHeight: 1.45 }}>
        {hazardClassMode ? SCOPE_HIERARCHY_NOTE_HAZARD_CLASS : SCOPE_HIERARCHY_NOTE}
      </Typography>
    </Paper>
  );
}

function RepresentativeCaseSummary({
  option,
  onChangeCase,
}: {
  option: RepresentativeSafetyCaseOption;
  onChangeCase: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.15, borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
        {REPRESENTATIVE_SAFETY_CASE_LABEL}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700, lineHeight: 1.45 }}>
        {[
          resolveCustomerFacingPatternLabel({
            preferredLabel: option.patternLabel || option.policyLabel,
            policyTypeId: option.policyType,
          }),
          customerFacingCameraLabel(option.cameraLabel) || option.cameraLabel,
        ].filter(Boolean).join(' · ')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.45 }}>
        {formatRepresentativeSeenLine(option)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.45 }}>
        {`Evidence: ${option.evidenceSummary}`}
      </Typography>
      {option.badges.length > 0 ? (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
          {option.badges.map((badge) => (
            <Chip key={`${option.value}-${badge}`} size="small" variant="outlined" label={badge} />
          ))}
        </Stack>
      ) : null}
      <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={onChangeCase}>
        Change representative evidence
      </Button>
    </Paper>
  );
}

function RepresentativeCaseOptionRow({ option }: { option: RepresentativeSafetyCaseOption }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.25, width: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
        {option.primaryLine}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.2, lineHeight: 1.35 }}>
        {option.secondaryLine}
      </Typography>
      {option.badges.length > 0 ? (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
          {option.badges.map((badge) => (
            <Chip key={`${option.value}-${badge}`} size="small" variant="outlined" label={badge} />
          ))}
        </Stack>
      ) : null}
      <Tooltip title={option.recordId} placement="left">
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.35, lineHeight: 1.3 }}>
          {`Record: ${option.recordId}`}
        </Typography>
      </Tooltip>
    </Box>
  );
}

export default function ProofScopePanel({
  reviewRecordPolicyFilter: _reviewRecordPolicyFilter,
  onReviewRecordPolicyFilterChange,
  reviewRecordPolicyOptions: _reviewRecordPolicyOptions,
  reviewRecordCameraFilter: _reviewRecordCameraFilter,
  onReviewRecordCameraFilterChange,
  reviewRecordCameraOptions: _reviewRecordCameraOptions,
  filteredSelectableReviewIncidents,
  activeRepresentativeCase,
  selectedReviewIncidentIds,
  onSelectedReviewIncidentIdsChange,
  reviewScopeIncidentsLoading,
  selectedIncidentScopeIsValid,
  selectedIncidentScopeInvalidReason,
  proofScopePolicyTypeDraft: _proofScopePolicyTypeDraft,
  proofScopePolicySelectValue,
  onProofScopePolicyTypeDraftChange,
  policyTypeOptions,
  reportFiltersScopedToCamera,
  proofScopeZoneDraft,
  onProofScopeZoneDraftChange,
  zoneOptions,
  proofScopeFromDraft,
  onProofScopeFromDraftChange,
  proofScopeToDraft,
  onProofScopeToDraftChange,
  currentSelectionSummary,
  reportScopeHierarchy,
  scopeCarriedFromReview,
  reportScopeDisplayLines,
  reportScopeResolutionHelper,
  reviewRecordCameraSelectValue,
  reviewRecordCameraOptionsResolved,
  filtersExpanded,
  onToggleFiltersExpanded,
  onApplyProofScopeSelection,
  proofScopeDirty,
  disableUpdateReport,
  onResetFilters,
  onOpenSelectedIncident,
  selectedIncidentReviewHref,
  onBackToReview,
  safetyReportTypeMode,
  onSafetyReportTypeModeChange,
  hazardClassKey,
  onHazardClassKeyChange,
}: Props) {
  const hazardClassMode = safetyReportTypeMode === 'hazard_class';
  const [changeCasePickerOpen, setChangeCasePickerOpen] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');

  const selectedValue = String(selectedReviewIncidentIds[0] || '').trim();
  const decoratedCandidates = useMemo(
    () => decorateRepresentativeCaseCandidates(filteredSelectableReviewIncidents, selectedValue),
    [filteredSelectableReviewIncidents, selectedValue],
  );
  const { visible: visibleCandidates, total: candidateTotal, truncated: candidatesTruncated } = useMemo(
    () => limitRepresentativeCaseCandidates(decoratedCandidates, candidateSearch),
    [candidateSearch, decoratedCandidates],
  );

  const activeCase = useMemo(() => {
    if (activeRepresentativeCase) {
      return decorateRepresentativeCaseCandidates([activeRepresentativeCase], selectedValue)[0] || activeRepresentativeCase;
    }
    return null;
  }, [activeRepresentativeCase, selectedValue]);

  const handlePolicyDraftChange = (value: string) => {
    onProofScopePolicyTypeDraftChange(value);
    onReviewRecordPolicyFilterChange(value);
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 2.5 }}>
      <Stack spacing={1.25}>
        <SafetyReportTypeControls
          reportTypeMode={safetyReportTypeMode}
          onReportTypeModeChange={onSafetyReportTypeModeChange}
          hazardClassKey={hazardClassKey}
          onHazardClassKeyChange={onHazardClassKeyChange}
        />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
              {REPORT_SCOPE_PANEL_TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45 }}>
              {filtersExpanded ? REPORT_SCOPE_INTRO_EXPANDED : REPORT_SCOPE_INTRO_COLLAPSED}
            </Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={onToggleFiltersExpanded} sx={{ flexShrink: 0 }}>
            {filtersExpanded ? HIDE_REPORT_SCOPE_BUTTON : CHANGE_REPORT_SCOPE_BUTTON}
          </Button>
        </Box>

        {!filtersExpanded ? (
          <Stack spacing={1}>
            <CompactReportScopeSummary
              lines={reportScopeDisplayLines}
              scopeCarriedFromReview={scopeCarriedFromReview}
            />
            {reportScopeResolutionHelper ? (
              <Alert severity="warning" sx={{ py: 0.35 }}>
                {reportScopeResolutionHelper}
              </Alert>
            ) : null}
          </Stack>
        ) : null}

        <Collapse in={filtersExpanded}>
          <Stack spacing={1.25}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
              {ADVANCED_FILTERS_HEADING}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {CHANGE_SCOPE_INTRO}
            </Typography>
            <TextField
              select
              size="small"
              label={CAMERA_FIELD_LABEL}
              value={reviewRecordCameraSelectValue}
              onChange={(event) => onReviewRecordCameraFilterChange(event.target.value)}
              helperText={reportFiltersScopedToCamera ? CAMERA_FIELD_HELPER_SCOPED : CAMERA_FIELD_HELPER}
              SelectProps={{ displayEmpty: false }}
            >
              <MenuItem value="">All cameras</MenuItem>
              {reviewRecordCameraOptionsResolved.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {customerFacingCameraLabel(option.label) || option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={AREA_FIELD_LABEL}
              value={proofScopeZoneDraft}
              onChange={(event) => onProofScopeZoneDraftChange(event.target.value)}
              helperText={reportFiltersScopedToCamera ? AREA_FIELD_HELPER_SCOPED : AREA_FIELD_HELPER}
            >
              <MenuItem value="">{ALL_AREAS_LABEL}</MenuItem>
              {zoneOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {customerFacingAreaLabel(option.label) || option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={SAFETY_POLICY_FIELD_LABEL}
              value={proofScopePolicySelectValue}
              onChange={(event) => handlePolicyDraftChange(event.target.value)}
              helperText={reportFiltersScopedToCamera ? SAFETY_POLICY_FIELD_HELPER_SCOPED : SAFETY_POLICY_FIELD_HELPER}
            >
              <MenuItem value="">{ALL_CONFIGURED_POLICIES_LABEL}</MenuItem>
              {policyTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {customerFacingSafetyRuleLabel(option.label)}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1,
              }}
            >
              <TextField
                size="small"
                label="Start time"
                type="datetime-local"
                value={proofScopeFromDraft}
                onChange={(event) => onProofScopeFromDraftChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Time window start for this report."
              />
              <TextField
                size="small"
                label="End time"
                type="datetime-local"
                value={proofScopeToDraft}
                onChange={(event) => onProofScopeToDraftChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Time window end for this report."
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.02 }}>
                {REPRESENTATIVE_SAFETY_CASE_LABEL}
              </Typography>
              {hazardClassMode ? (
                <Alert severity="info" sx={{ mt: 0.75, py: 0.35 }}>
                  {REPRESENTATIVE_CASE_HAZARD_CLASS_NOTE}
                </Alert>
              ) : (
              <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.45 }}>
                {changeCasePickerOpen ? REPRESENTATIVE_CASE_PICKER_INTRO : REPRESENTATIVE_CASE_AUTO_HELPER}
              </Typography>

              {!changeCasePickerOpen ? (
                activeCase ? (
                  <RepresentativeCaseSummary
                    option={activeCase}
                    onChangeCase={() => {
                      setChangeCasePickerOpen(true);
                      setCandidateSearch('');
                    }}
                  />
                ) : (
                  <Paper variant="outlined" sx={{ p: 1.15, mt: 0.75, borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                      No representative evidence is selected yet.
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mt: 0.75 }}
                      onClick={() => setChangeCasePickerOpen(true)}
                      disabled={reviewScopeIncidentsLoading}
                    >
                      Change representative evidence
                    </Button>
                  </Paper>
                )
              ) : (
                <Stack spacing={1} sx={{ mt: 0.75 }} data-testid="representative-case-picker-panel">
                  <TextField
                    size="small"
                    label={REPRESENTATIVE_CASE_FILTER_LABEL}
                    value={candidateSearch}
                    onChange={(event) => setCandidateSearch(event.target.value)}
                    placeholder={REPRESENTATIVE_CASE_FILTER_PLACEHOLDER}
                    helperText={reviewScopeIncidentsLoading
                      ? 'Evidence records for this window are still loading.'
                      : REPRESENTATIVE_CASE_FILTER_HELPER}
                  />
                  <Autocomplete
                    options={visibleCandidates}
                    value={activeCase}
                    openOnFocus
                    onChange={(_, value) => {
                      onSelectedReviewIncidentIdsChange(value ? [value.value] : []);
                    }}
                    getOptionLabel={(option) => String(option?.primaryLine || option?.label || option?.value || '')}
                    isOptionEqualToValue={(option, value) => String(option?.value || '') === String(value?.value || '')}
                    filterOptions={(options) => options}
                    ListboxProps={{ style: { maxHeight: 320 } }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.value}>
                        <RepresentativeCaseOptionRow option={option} />
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={REPRESENTATIVE_CASE_LIST_LABEL}
                        placeholder={visibleCandidates.length > 0
                          ? REPRESENTATIVE_CASE_LIST_PLACEHOLDER
                          : 'No evidence records match this filter'}
                        helperText="Each row is one evidence record in the selected window."
                        InputProps={{
                          ...params.InputProps,
                          inputProps: {
                            ...params.inputProps,
                            'data-testid': 'representative-safety-case-picker',
                            'aria-label': 'Evidence record list picker',
                          },
                        }}
                      />
                    )}
                    noOptionsText={reviewScopeIncidentsLoading ? 'Loading evidence records…' : 'No evidence records match this filter'}
                  />
                  {!reviewScopeIncidentsLoading ? (
                    <Alert severity="info" sx={{ py: 0.25 }}>
                      {buildRepresentativeCaseListHint({
                        visibleCount: visibleCandidates.length,
                        totalCount: candidateTotal,
                        truncated: candidatesTruncated,
                      })}
                    </Alert>
                  ) : null}
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setChangeCasePickerOpen(false)}
                      disabled={!selectedReviewIncidentIds.length}
                    >
                      Use this case
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setChangeCasePickerOpen(false);
                        setCandidateSearch('');
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              )}
              </>
              )}
            </Box>

            {!hazardClassMode && selectedReviewIncidentIds.length > 0 && !selectedIncidentScopeIsValid ? (
              <Alert severity="warning">{selectedIncidentScopeInvalidReason}</Alert>
            ) : null}
            <ReportScopeHierarchyPanel
              hierarchy={reportScopeHierarchy}
              scopeCarriedFromReview={scopeCarriedFromReview}
              hazardClassMode={hazardClassMode}
            />
            {reportScopeResolutionHelper ? (
              <Alert severity="warning" sx={{ py: 0.35 }}>
                {reportScopeResolutionHelper}
              </Alert>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {currentSelectionSummary}
            </Typography>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} useFlexGap flexWrap="wrap" justifyContent="space-between">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={onApplyProofScopeSelection}
                  disabled={disableUpdateReport}
                >
                  Update Report
                </Button>
                <Button
                  variant="outlined"
                  onClick={onResetFilters}
                  disabled={!proofScopeDirty}
                >
                  Reset Filters
                </Button>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                {!hazardClassMode ? (
                  <Button
                    variant="contained"
                    onClick={onOpenSelectedIncident}
                    disabled={!selectedIncidentReviewHref}
                  >
                    Open Review
                  </Button>
                ) : null}
                <Button variant="outlined" onClick={onBackToReview}>
                  Back to Review
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
